/**
 * Rule & Decision Audit View — item 15 (Asana 1216373781992284), spec §10.4: "RuleSet version
 * history with diffs, per-EUC decision log, override log."
 *
 * Two panels, sharing one screen because they answer the same underlying question — "why does
 * this EUC look the way it does right now" — from two different angles:
 *
 *   1. Ruleset version history + diffs. Item 10 already made every publish an immutable version
 *      row with the FULL effective ruleset stored (not the sparse overrides a consultant actually
 *      typed) — that matters here because a field carried forward unchanged from an earlier
 *      version must NOT show up as "changed" just because a later proposal's sparse overrides
 *      object happens to still mention it. rulesetDiff.js does the real comparison server-side;
 *      this screen just renders it.
 *   2. The decision/override log. A NEW, append-only framework_decision_log table — separate from
 *      disposition_overrides and commit_log, which are both "latest state only" and get
 *      overwritten per EUC. One row per override or commit action, going forward. Filtering by
 *      euc_id gives "this EUC's decision history"; filtering by event_type gives "the override
 *      log" (or "the commit log") across the whole portfolio.
 *
 * Honest scope note, surfaced directly in this screen rather than left implicit: the decision log
 * starts recording from when this shipped. It is NOT a retroactive backfill of every historical
 * ingest/publish re-score — only override and commit actions are logged, because those are the
 * two places a human deliberately changed something. Logging every scoring pass too would mean
 * writing a row per EUC on every ingest/publish (154+ rows each time) — flagged as explicit
 * follow-up in the migration's own doc comment, not silently built or silently skipped here.
 */

import { useEffect, useState } from 'react';
import { useDecisionAudit } from './useDecisionAudit';
import type { DecisionDiffEntry, DecisionEventType, DecisionLogEvent, VersionDiffResult } from './useDecisionAudit';
import type { RulesetVersionSummary } from './useRuleConfig';
import { color, font } from '../tokens';

const C = {
  accent: color.accent, deep: color.navy, ink: '#1e1b3a',
  body: '#4a4966', subtle: color.textMuted, label: '#7c7a95', border: color.cardBorder, soft: '#f9fafb', surface: color.surface,
};
const mono = 'JetBrains Mono, monospace';

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 18, marginBottom: 18 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: C.subtle, marginTop: 3 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function ScopeNote({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, color: '#9a5b00', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 12px', marginBottom: 18, lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

function fmtVal(v: any): string {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'enabled' : 'disabled';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function DiffEntryRow({ entry }: { entry: DecisionDiffEntry }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
      <span style={{ fontFamily: mono, fontWeight: 800, color: C.ink, minWidth: 44 }}>{entry.id}</span>
      <span style={{ color: C.subtle, minWidth: 90 }}>{entry.field}</span>
      {entry.added || entry.removed ? (
        <span style={{ color: C.body, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(entry.added || []).map((a) => (
            <span key={`add-${a}`} style={{ color: '#166534', background: '#dcfce7', borderRadius: 999, padding: '1px 8px', fontFamily: mono, fontSize: 11 }}>+ {a}</span>
          ))}
          {(entry.removed || []).map((r) => (
            <span key={`rm-${r}`} style={{ color: '#991b1b', background: '#fee2e2', borderRadius: 999, padding: '1px 8px', fontFamily: mono, fontSize: 11 }}>− {r}</span>
          ))}
        </span>
      ) : (
        <span style={{ color: C.body, fontFamily: mono }}>
          {fmtVal(entry.from)} <span style={{ color: C.label }}>→</span> {fmtVal(entry.to)}
        </span>
      )}
    </div>
  );
}

function DiffPanel({ result }: { result: VersionDiffResult }) {
  const sections: { key: keyof VersionDiffResult['diff']; label: string }[] = [
    { key: 'factors', label: 'Factors' },
    { key: 'gates', label: 'Gates' },
    { key: 'boxes', label: 'Boxes' },
    { key: 'thresholds', label: 'Thresholds' },
  ];
  if (result.diff.changed_count === 0) {
    return <div style={{ fontSize: 12, color: C.subtle, padding: '8px 0' }}>No differences from {result.against_label}.</div>;
  }
  return (
    <div>
      {sections.map(({ key, label }) => {
        const entries = result.diff[key] as DecisionDiffEntry[];
        if (!entries.length) return null;
        return (
          <div key={key} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
            {entries.map((e, i) => <DiffEntryRow key={`${key}-${e.id}-${e.field}-${i}`} entry={e} />)}
          </div>
        );
      })}
    </div>
  );
}

function VersionRow({ v, audit }: { v: RulesetVersionSummary; audit: ReturnType<typeof useDecisionAudit> }) {
  const [open, setOpen] = useState(false);
  const result = audit.diffs[v.version];
  const error = audit.diffErrors[v.version];
  const loading = audit.diffLoading === v.version;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !result && !loading) audit.loadDiff(v.version);
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.soft }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: mono, fontSize: 12.5, fontWeight: 800, color: C.ink }}>v{v.version}</span>
          <span style={{ fontSize: 11, color: C.subtle }}>parent {v.parent_version ?? '—'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: C.subtle }}>
            published {new Date(v.published_at).toLocaleString()} by {v.published_by?.name || v.published_by?.email || `user #${v.published_by?.id}`}
          </span>
          <button onClick={toggle} style={{ fontSize: 11, fontWeight: 700, color: C.accent, background: 'transparent', border: `1px solid ${C.accent}`, borderRadius: 999, padding: '3px 10px', cursor: 'pointer' }}>
            {loading ? 'Loading…' : open ? 'Hide diff' : 'View diff'}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: 12 }}>
          {error ? (
            <div style={{ fontSize: 12, color: '#991b1b' }}>{error}</div>
          ) : result ? (
            <>
              <div style={{ fontSize: 11, color: C.label, marginBottom: 8 }}>Compared against {result.against_label}</div>
              <DiffPanel result={result} />
            </>
          ) : (
            <div style={{ fontSize: 12, color: C.subtle }}>Loading…</div>
          )}
        </div>
      )}
    </div>
  );
}

function EventBadge({ type }: { type: DecisionEventType }) {
  const style = type === 'override'
    ? { bg: '#fef3c7', fg: '#9a5b00', label: 'OVERRIDE' }
    : { bg: '#ede9fe', fg: C.accent, label: 'COMMIT' };
  return (
    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 999, background: style.bg, color: style.fg }}>
      {style.label}
    </span>
  );
}

function eventSummary(e: DecisionLogEvent): string {
  const p = e.payload || {};
  if (e.event_type === 'override') {
    const from = p.original_disposition ? ` (was ${p.original_disposition})` : '';
    return `→ ${p.disposition || '?'}${from}${p.reason ? ` — ${p.reason}` : ''}`;
  }
  const routing = Array.isArray(p.routing) ? p.routing.map((r: any) => r.path || 'none').join(', ') : '';
  return `${p.decision || '?'} → ${p.disposition || '?'}${routing ? ` — routed ${routing}` : ''}${p.reason ? ` — ${p.reason}` : ''}`;
}

function LogRow({ e }: { e: DecisionLogEvent }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 76px 90px 1fr 140px', alignItems: 'baseline', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
      <span style={{ fontFamily: mono, fontWeight: 800, color: C.ink }}>{e.euc_id}</span>
      <EventBadge type={e.event_type} />
      <span style={{ fontSize: 11, color: C.subtle }}>{new Date(e.at).toLocaleString()}</span>
      <span style={{ color: C.body }}>{eventSummary(e)}</span>
      <span style={{ fontSize: 11, color: C.label, textAlign: 'right' }}>{e.actor?.name || e.actor?.email || `user #${e.actor?.id}`}</span>
    </div>
  );
}

export default function AuditView() {
  const audit = useDecisionAudit();

  useEffect(() => {
    audit.loadVersions();
  }, []);

  // The decision log is project-scoped (QA finding 2026-08-05) — re-fetch when the user switches
  // project, otherwise this screen keeps showing the project it was first opened on.
  useEffect(() => {
    audit.loadLog();
  }, [audit.projectId]);

  const versionsDesc = [...audit.versions].sort((a, b) => b.version - a.version);

  return (
    <div style={{ fontFamily: font.ui, maxWidth: 920 }}>
      <ScopeNote>
        The decision log below records override and commit actions going forward from when this
        screen shipped — it is not a retroactive backfill of every historical ingest/publish
        re-score, only of the moments a human deliberately overrode or committed a disposition.
      </ScopeNote>

      <SectionCard title="Ruleset version history" subtitle="Immutable — every publish adds a row and never edits or removes an earlier one. Diffs compare each version's full effective ruleset, not the sparse overrides a proposal touched.">
        {!audit.versionsLoaded ? (
          <div style={{ fontSize: 12, color: C.subtle }}>Loading…</div>
        ) : audit.versionsError ? (
          <div style={{ fontSize: 12, color: '#991b1b' }}>{audit.versionsError}</div>
        ) : versionsDesc.length === 0 ? (
          <div style={{ fontSize: 12, color: C.subtle }}>Nothing published yet — this tenant is still on the unmodified default ruleset.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {versionsDesc.map((v) => <VersionRow key={v.version} v={v} audit={audit} />)}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Decision & override log" subtitle="Per-EUC decision history, or the portfolio-wide override/commit log — filter by either below. Newest first.">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <input
            value={audit.eucFilter}
            onChange={(ev) => audit.setEucFilter(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') audit.loadLog(); }}
            placeholder="Filter by EUC ID…"
            style={{ fontSize: 12, fontFamily: mono, padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.border}`, minWidth: 180 }}
          />
          <select
            value={audit.eventTypeFilter}
            onChange={(ev) => audit.setEventTypeFilter(ev.target.value as any)}
            style={{ fontSize: 12, padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.border}`, color: C.body }}
          >
            <option value="">All events</option>
            <option value="override">Overrides only</option>
            <option value="commit">Commits only</option>
          </select>
          <button
            onClick={() => audit.loadLog()}
            style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: C.accent, border: 'none', borderRadius: 999, padding: '7px 16px', cursor: 'pointer' }}
          >
            {audit.logLoading ? 'Loading…' : 'Apply'}
          </button>
          {(audit.eucFilter || audit.eventTypeFilter) && (
            <button
              onClick={() => { audit.setEucFilter(''); audit.setEventTypeFilter(''); }}
              style={{ fontSize: 12, color: C.subtle, background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>

        {audit.logError ? (
          <div style={{ fontSize: 12, color: '#991b1b' }}>{audit.logError}</div>
        ) : !audit.logLoaded ? (
          <div style={{ fontSize: 12, color: C.subtle }}>Loading…</div>
        ) : audit.log.length === 0 ? (
          <div style={{ fontSize: 12, color: C.subtle }}>No decision events match this filter.</div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 76px 90px 1fr 140px', gap: 10, padding: '0 0 6px', fontSize: 11, fontWeight: 700, color: C.label, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${C.border}` }}>
              <span>EUC</span><span>Event</span><span>When</span><span>What changed</span><span style={{ textAlign: 'right' }}>Actor</span>
            </div>
            {audit.log.map((e) => <LogRow key={e.id} e={e} />)}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
